import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { AlertCircle, RefreshCw, Home } from 'lucide-react'
import * as Sentry from '@sentry/react'
import { contactUSEmail } from '@/constants'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    })

    this.setState({
      error,
      errorInfo,
    })
  }

  handleReload = (): void => {
    window.location.reload()
  }

  handleGoHome = (): void => {
    window.location.href = '/'
  }

  render(): ReactNode {
    const { hasError } = this.state
    const { children } = this.props

    if (hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ss-neutral-50 to-ss-neutral-100 p-4">
          <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-ss-critical-50 rounded-full flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-ss-critical-200" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-ss-neutral-950 mb-3 text-center">
              Oops! Something went wrong
            </h1>

            <p className="text-sm text-ss-neutral-600 mb-6 text-center leading-relaxed">
              We encountered an unexpected error.
            </p>

            <div className="flex flex-col gap-3">
              <Button onClick={this.handleReload} className="w-full" size="lg">
                <RefreshCw className="mr-2 h-4 w-4" />
                Reload Page
              </Button>
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="w-full"
                size="lg"
              >
                <Home className="mr-2 h-4 w-4" />
                Go to Homepage
              </Button>
            </div>

            {/* Support Info */}
            <div className="mt-6 p-4 bg-ss-blue-50 rounded-lg border border-ss-blue-100">
              <p className="text-xs text-ss-neutral-700 text-center">
                Need help?{' '}
                <a
                  href={`mailto:${contactUSEmail}`}
                  className="text-ss-blue-500 hover:text-ss-blue-600 font-medium underline"
                >
                  Contact Support
                </a>
              </p>
            </div>
          </div>
        </div>
      )
    }

    return children
  }
}

export default ErrorBoundary
